'use client';
/**
 * LE CARNET DES CORRESPONDANTS EXTERNES, ET LE RELEVÉ MENSUEL.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Demande d'origine (RQ, 07/10/2026) : « le centre de dialyse avec qui nous
 * travaillons tous les mois m'envoie une prescription sans prélèvement.
 * J'aimerais pouvoir le signaler et à la fin générer la liste des NC du mois
 * pour ce correspondant et la lui envoyer. » Puis : « en plus de l'envoi par
 * mail, je souhaite simplement pouvoir imprimer un pdf que j'enverrais. »
 *
 * ── Pourquoi le carnet ne JUGE pas (les laboratoires y sont un onglet) ─────
 *
 * Parce que celui-là JUGE et celui-ci NON. Le registre du §6.7 porte
 * l'accréditation, sa portée, la justification du choix et six critères de
 * compétence ; ce carnet ne porte que l'identité et les coordonnées — et il
 * accueille ce que l'autre ne peut pas accueillir : une clinique conventionnée,
 * un centre de dialyse, un médecin prescripteur extérieur, un préleveur. Les
 * fondre aurait fait passer pour évalué un laboratoire dont personne n'a
 * regardé la portée d'accréditation : c'est le constat qu'un auditeur vient
 * chercher.
 *
 * ── Ce que cet écran refuse de faire ──────────────────────────────────────
 *
 *  · LAISSER CROIRE QU'UN RELEVÉ TÉLÉCHARGÉ EST UN RELEVÉ ENVOYÉ. Les deux
 *    gestes sont distincts et nommés distinctement : « Télécharger le PDF »
 *    produit le document (le laboratoire l'adresse lui-même), « Envoyer par
 *    courriel » l'expédie. Le registre des relevés dit lequel a eu lieu.
 *
 *  · PROPOSER UN ENVOI QUI NE PARTIRA PAS. Le bouton est grisé — et DIT
 *    pourquoi — quand le correspondant n'a aucune adresse, ou quand aucun
 *    serveur de courriel n'est réglé. C'est ce qui manquait aux deux flux
 *    publics avant le lot des notifications : on cliquait, l'écran disait
 *    merci, et rien ne partait.
 *
 *  · CONFONDRE UN RELEVÉ VIDE AVEC UNE ERREUR. « Aucune non-conformité ne lui
 *    est imputée sur ce mois » est un résultat, et le document le dit.
 *
 * ── Mois, période ou sélection (08/10/2026) ───────────────────────────────
 *
 * « Adresser selon une plage donnée ou une sélection de NC. » Le relevé se
 * choisit par un segment à trois modes : le mois (la règle), une période du …
 * au … (bornes incluses), ou une sélection — les non-conformités imputées à ce
 * correspondant sont listées avec une case chacune, TOUS leurs dossiers
 * partent. Le serveur refuse en le disant une sélection qui nommerait une NC
 * d'un autre tiers ; l'écran, lui, ne propose que les siennes.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import {
  I, card, inputStyle, EmptyState, FilterBar, FieldError, useBusy, Help, RefISO, Selecteur, BoutonCreer, useConfirmation,
} from '@/components/ui';
import { lbl, pill } from './ressources/commun';
import { EnteteEcran } from '@/components/EnteteEcran';
import { oublierCarnet } from '@/components/ChoixCorrespondant';
import { ConventionsContact } from '@/components/ConventionsContact';
import { useFormulaire } from '@/components/Formulaire';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { Segment } from '@/shared/Segment';
import { Tableau } from '@/shared/Tableau';

type TypeDef = { code: string; label: string; aide: string; registreEvaluateur?: string };
type UsageDef = { code: string; label: string };
type Rubrique = { code: string; label: string; type?: 'texte' | 'case' };
type Courriel = { usage: string; adresse: string; nom?: string };
type Ligne = {
  documentId: string; code: string; nom: string; types: string[]; sens: string;
  adresse: string; ville: string; telephone: string; contact: string;
  courriels: Courriel[]; rubriques: Record<string, string>; rubriquesOffertes: Rubrique[];
  actif: boolean; note: string; manques: string[];
  /** Les onglets où la ligne paraît (une ligne à deux types peut en avoir deux). */
  familles: string[];
  /** Calculé par le serveur : le RQ tout, les achats les seuls fournisseurs. */
  modifiable: boolean;
  destinatairesQualite: { adresses: string[]; usage: string | null; repli: boolean };
  compte: { ncs: number; dossiers: number; ouvertes: number; signalees?: number; reclamations?: number };
};
type Famille = { code: string; label: string; types: string[] };
type Carnet = {
  lignes: Ligne[];
  catalogues: {
    types: TypeDef[]; familles: Famille[]; usagesCourriel: UsageDef[]; rubriquesParType: Record<string, Rubrique[]>;
    /** Les types que CETTE personne peut poser — l'écran ne propose pas ce que le serveur refuserait. */
    typesEcriture: string[];
  };
  compte: { total: number; actifs: number; sansCourriel: number; avecNc: number };
  orphelines: { ref: string; correspondantCode: string }[];
  /** Les conventions et événements reliés par code (L3 ; tous depuis le 12/10/2026). */
  conventions: Record<string, { nom: string; nature: string; description: string; actif: boolean }[]>;
  /** Les examens que le catalogue déclare sous-traités — l'écart à rattacher. */
  catalogue: { sousTraites: number; codes: string[] };
  aujourdhui: string;
  peutEcrire: boolean;
  peutReleve: boolean;
};
type LigneReleve = {
  ncRef: string; ncTitre: string; date: string; gravite: string; statut: string;
  dossier: string; suites: string;
};
type ModePeriode = 'mois' | 'plage' | 'selection';
type Releve = {
  correspondant: { code: string; nom: string };
  mois: string;
  periode: { mode: ModePeriode; du: string; au: string; ncRefs: string[]; libelle: string; cle: string };
  lignes: LigneReleve[];
  compte: {
    ncs: number; dossiers: number; parGravite: Record<string, number>;
    moisPrecedents: { mois: string; ncs: number; dossiers: number }[];
  };
};
type NcImputee = { ref: string; titre: string; date: string; gravite: string; statut: string; dossiers: number };
type DossierReleve = {
  releve: Releve;
  correspondant: Ligne;
  moisLabel: string;
  titre: string;
  phraseVide: string;
  /** Les NC imputées à ce correspondant — ce qu'on peut cocher en mode sélection. */
  imputees: NcImputee[];
  anterieurs: {
    ref: string; mois: string; periode: string; mode: ModePeriode; canal: string; envoye: boolean;
    datePrepare: string; dateEnvoi: string; destinataires: string[]; dossiers: number | null;
  }[];
  /* La forme EXACTE de `etatCourriel` (`backend/src/courriel-etat.ts`) :
     `configure` et `consequence`. Les renommer ici au jugé a eu un effet
     silencieux et grave, corrigé le 07/10/2026 avant mise en service :
     `pret !== false` vaut VRAI sur un champ absent, donc le bouton d'envoi
     restait actif alors qu'aucun serveur n'est réglé. Un bouton qui promet un
     envoi impossible est précisément le défaut que tout le lot des
     notifications existe pour fermer. */
  courriel: { configure: boolean; consequence: string; manques: string[] };
  /** Ce qui a DÉJÀ été adressé pour cette période précise — l'avertissement se
   *  lit AVANT le clic, pas après (arbitrage du 08/10/2026). */
  dejaAdresse: { existe: boolean; phrase: string };
  peutEcrire: boolean;
};

const SENS: Record<string, string> = {
  envoie: 'Nous lui envoyons des examens',
  recoit: 'Il nous envoie des échantillons ou des prescriptions',
  les_deux: 'Dans les deux sens',
};
const encart: React.CSSProperties = {
  background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)',
  borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5,
  color: 'var(--text-body)', lineHeight: 1.5,
};
const AIDE_USAGE = 'L’usage d’une adresse n’est pas décoratif : un relevé qualité envoyé à la boîte '
  + 'des résultats est dépouillé par un secrétariat qui classe, pas par la personne qui répond d’un '
  + 'écart. Le relevé cherche d’abord l’adresse « qualité », et ne se replie sur une autre qu’en le disant.';

const AIDE_PERIODE = 'Mois : le relevé mensuel, avec le rappel des trois mois précédents. Période : du … au …, '
  + 'bornes incluses. Sélection : les non-conformités que vous cochez, avec tous leurs dossiers, quelle que '
  + 'soit leur date.';

/** Le mois précédent celui d'aujourd'hui — le relevé se fait « à la fin du mois ». */
function moisParDefaut(aujourdhui: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(aujourdhui);
  if (!m) return '';
  const an = Number(m[1]); const mo = Number(m[2]);
  return mo <= 1 ? `${an - 1}-12` : `${an}-${String(mo - 1).padStart(2, '0')}`;
}

/* ───────────────────────────── La fiche ───────────────────────────── */

function Formulaire({ ligne, catalogues, onFait, onAnnule }: {
  ligne: Ligne | null; catalogues: Carnet['catalogues']; onFait: () => void; onAnnule: () => void;
}) {
  const typesProposes = catalogues.types.filter((t) => (catalogues.typesEcriture || []).includes(t.code));
  const [f, setF] = useState({
    nom: ligne?.nom || '',
    /* Une personne qui ne peut poser qu'UN type (les achats) le trouve coché :
       lui demander de cocher la seule case possible est une question sans choix. */
    types: ligne?.types || (typesProposes.length === 1 ? [typesProposes[0].code] : ([] as string[])),
    sens: ligne?.sens || 'recoit',
    adresse: ligne?.adresse || '', ville: ligne?.ville || '', telephone: ligne?.telephone || '',
    contact: ligne?.contact || '', note: ligne?.note || '', actif: ligne ? ligne.actif : true,
  });
  const [courriels, setCourriels] = useState<Courriel[]>(ligne?.courriels?.length
    ? ligne.courriels : [{ usage: 'qualite', adresse: '' }]);
  const [rubriques, setRubriques] = useState<Record<string, string>>(ligne?.rubriques || {});
  /* ⚠️ CE QUE L'ÉCRAN A LU, FIGÉ À L'OUVERTURE (14/10/2026) : le moteur n'écrit
     que ce qui a changé depuis cette lecture — une adresse ajoutée par un
     autre pendant la saisie n'est plus effacée, elle fait un conflit. */
  const [base] = useState<Record<string, unknown>>(() => ({
    ...f, courriels: courriels.filter((c) => c.adresse.trim()), rubriques: { ...rubriques },
  }));
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const declaration = useMemo(() => [
    { id: 'cor-nom', label: 'Nom', valeur: f.nom, requis: true },
  ], [f.nom]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  /* Les rubriques offertes suivent les types COCHÉS : décocher « médecin »
     retire la spécialité, et le serveur ne la gardera pas. */
  const offertes = useMemo(() => {
    const vues = new Set<string>(); const out: Rubrique[] = [];
    for (const t of f.types) {
      for (const r of catalogues.rubriquesParType[t] || []) {
        if (vues.has(r.code)) continue;
        vues.add(r.code); out.push(r);
      }
    }
    return out;
  }, [f.types, catalogues.rubriquesParType]);

  const basculerType = (code: string) => setF((x) => ({
    ...x, types: x.types.includes(code) ? x.types.filter((t) => t !== code) : [...x.types, code],
  }));

  const enregistrer = () => run('save', async () => {
    if (!verifier()) return;
    setErreur('');
    const corps = {
      ...f,
      courriels: courriels.filter((c) => c.adresse.trim()),
      rubriques: Object.fromEntries(offertes.map((r) => [r.code, rubriques[r.code] || ''])),
      ...(ligne ? { base } : {}),
    };
    const r = await fetchEcriture(`/api/ged/ged/correspondants${ligne ? `/${ligne.documentId}` : ''}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
    });
    if (!r.ok) {
      /* Le motif du serveur est AFFICHÉ tel quel : il nomme l'adresse refusée.
         Un « échec de l'enregistrement » générique obligerait à chercher
         laquelle des quatre adresses est mal tapée. */
      const j = await r.json().catch(() => null);
      setErreur(j?.error?.message || 'L’enregistrement n’a pas abouti.');
      return;
    }
    onFait();
  });

  /* ⚠️ LE FORMULAIRE S'OUVRE EN HAUT DE PAGE, LA LIGNE CLIQUÉE EST EN BAS (RQ,
     10/10/2026 : « le bouton Modifier ne fonctionne pas »). Il fonctionnait :
     il ouvrait hors de l'écran, sans défilement, et rien ne le disait. On
     amène le formulaire sous les yeux à l'ouverture. */
  useEffect(() => { refFormulaire.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, [refFormulaire]);

  return (
    <div ref={refFormulaire} style={{ ...card, padding: 18, display: 'grid', gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>
        {ligne ? `Modifier ${ligne.nom}` : 'Nouveau correspondant'}
      </h2>
      <Bandeau />

      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl} htmlFor="cor-nom">Nom <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <input id="cor-nom" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} style={inputStyle} />
          {ligne && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
              Code <strong style={{ fontFamily: 'var(--font-mono)' }}>{ligne.code}</strong> — il ne change
              jamais : c’est lui que portent les non-conformités déjà imputées.
            </div>
          )}
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <span style={lbl}>Types <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>(plusieurs possibles)</span></span>
          <div style={{ display: 'grid', gap: 5 }}>
            {typesProposes.map((t) => (
              <label key={t.code} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
                <input type="checkbox" checked={f.types.includes(t.code)} onChange={() => basculerType(t.code)} />
                <span>
                  <strong style={{ color: 'var(--text-strong)' }}>{t.label}</strong>
                  <span style={{ color: 'var(--text-muted)' }}> — {t.aide}</span>
                  {t.registreEvaluateur && (
                    <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      {' '}Son évaluation reste au registre « {t.registreEvaluateur} ».
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label style={lbl} htmlFor="cor-sens">Sens de la relation</label>
          <Selecteur id="cor-sens" value={f.sens} onChange={(v) => setF({ ...f, sens: v })} style={inputStyle}>
            {Object.entries(SENS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Selecteur>
        </div>
        <div>
          <label style={lbl} htmlFor="cor-contact">Personne à qui l’on parle</label>
          <input id="cor-contact" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })}
            placeholder="Ex. Dr. S. — cadre du centre" style={inputStyle} />
        </div>
        <div>
          <label style={lbl} htmlFor="cor-tel">Téléphone</label>
          <input id="cor-tel" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={lbl} htmlFor="cor-ville">Ville</label>
          <input id="cor-ville" value={f.ville} onChange={(e) => setF({ ...f, ville: e.target.value })} style={inputStyle} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl} htmlFor="cor-adresse">Adresse</label>
          <textarea id="cor-adresse" value={f.adresse} rows={2} onChange={(e) => setF({ ...f, adresse: e.target.value })}
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <span style={lbl}><Help aide={AIDE_USAGE}>Adresses de courriel</Help></span>
          <div style={{ display: 'grid', gap: 6 }}>
            {courriels.map((c, i) => (
              /* Le rang fait la clé : une adresse en cours de frappe n'a pas
                 encore de valeur unique, et deux lignes vides partageraient
                 une clé vide. */
              <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 190px' }}>
                  <Selecteur value={c.usage} aria-label="Usage de l’adresse"
                    onChange={(v) => setCourriels(courriels.map((x, j) => (j === i ? { ...x, usage: v } : x)))}
                    style={inputStyle}>
                    {catalogues.usagesCourriel.map((u) => <option key={u.code} value={u.code}>{u.label}</option>)}
                  </Selecteur>
                </div>
                <div style={{ flex: '2 1 240px' }}>
                  <input value={c.adresse} aria-label="Adresse de courriel" placeholder="nom@domaine.tn"
                    onChange={(e) => setCourriels(courriels.map((x, j) => (j === i ? { ...x, adresse: e.target.value } : x)))}
                    style={inputStyle} />
                </div>
                <Bouton taille="sm" variante="contour" aria-label={`Retirer l’adresse ${c.adresse || i + 1}`}
                  onClick={() => setCourriels(courriels.filter((_, j) => j !== i))}>Retirer</Bouton>
              </div>
            ))}
            <Bouton taille="sm" style={{ width: 'fit-content' }}
              onClick={() => setCourriels([...courriels, { usage: 'qualite', adresse: '' }])}>
              <I n="plus" s={12} />Ajouter une adresse
            </Bouton>
          </div>
        </div>

        {offertes.length > 0 && (
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={lbl}>Rubriques propres aux types choisis</span>
            <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
              {offertes.map((r) => (
                r.type === 'case' ? (
                  /* Une COCHE (« oui » ou vide) : l'échographiste d'un médecin, lu par MoMent. */
                  <label key={r.code} style={{ display: 'inline-flex', gap: 7, alignItems: 'center', fontSize: 13, alignSelf: 'end', paddingBottom: 10 }}>
                    <input type="checkbox" checked={rubriques[r.code] === 'oui'}
                      onChange={(e) => setRubriques({ ...rubriques, [r.code]: e.target.checked ? 'oui' : '' })} />
                    {r.label}
                  </label>
                ) : (
                  <div key={r.code}>
                    <label style={lbl} htmlFor={`cor-r-${r.code}`}>{r.label}</label>
                    <input id={`cor-r-${r.code}`} value={rubriques[r.code] || ''}
                      onChange={(e) => setRubriques({ ...rubriques, [r.code]: e.target.value })} style={inputStyle} />
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl} htmlFor="cor-note">Note</label>
          <textarea id="cor-note" value={f.note} rows={2} onChange={(e) => setF({ ...f, note: e.target.value })}
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={f.actif} onChange={(e) => setF({ ...f, actif: e.target.checked })} />
          Correspondant actif
        </label>
      </div>

      <FieldError>{erreur}</FieldError>
      <PiedActions
        taille="sm"
        annuler={{ onClick: onAnnule }}
        principale={{
          libelle: ligne ? 'Enregistrer' : 'Créer le correspondant',
          libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(),
          onClick: () => void enregistrer(),
        }} />
    </div>
  );
}

/* ───────────────────────────── Le relevé ───────────────────────────── */

function PanneauReleve({ ligne, aujourdhui, onFerme }: {
  ligne: Ligne; aujourdhui: string; onFerme: () => void;
}) {
  const { showToast } = useGed();
  const [mode, setMode] = useState<ModePeriode>('mois');
  const [mois, setMois] = useState(moisParDefaut(aujourdhui));
  const [du, setDu] = useState(`${moisParDefaut(aujourdhui)}-01`);
  const [au, setAu] = useState(aujourdhui);
  const [choix, setChoix] = useState<string[]>([]);
  const [d, setD] = useState<DossierReleve | null>(null);
  const [chargement, setChargement] = useState(true);
  const [echec, setEchec] = useState('');
  const [tour, setTour] = useState(0);
  const { run, estOccupe } = useBusy();
  /* ⚠️ `{confirmation}` DOIT être rendu (voir plus bas), sinon `demander`
     attend une réponse que rien n'affiche et le geste reste suspendu. */
  const { demander, confirmation } = useConfirmation();

  /* Remise à l'état « en chargement » PENDANT LE RENDU, et non dans l'effet :
     un `setState` dans le corps d'un effet déclenche un rendu en cascade
     (React 19), et la règle est une ERREUR de lint dans ce dépôt. Changer de
     mois doit effacer le relevé précédent — sinon on lit octobre sous le titre
     de septembre pendant le temps de l'appel. */
  /* La période telle que le serveur la lit — la même pour l'aperçu, le CSV,
     le PDF et le courriel : un seul objet, sinon l'aperçu et le document
     pourraient ne pas couvrir la même chose. */
  const periode = mode === 'mois' ? { mode, mois }
    : mode === 'plage' ? { mode, du, au }
      : { mode, ncRefs: choix };
  const requete = new URLSearchParams(
    mode === 'mois' ? { mode, mois } : mode === 'plage' ? { mode, du, au } : { mode, ncRefs: choix.join(',') },
  ).toString();
  /* Une plage inversée ne part même pas au serveur : il la refuserait, mais
     l'écran peut le dire tout de suite, à côté des deux champs. */
  const plageInvalide = mode === 'plage' && (!du || !au || du > au);
  const selectionVide = mode === 'selection' && choix.length === 0;

  const cle = `${ligne.documentId}|${requete}|${tour}`;
  const [vue, setVue] = useState(cle);
  if (vue !== cle) { setVue(cle); setD(null); setChargement(!plageInvalide); setEchec(''); }

  useEffect(() => {
    let vivant = true;
    if (plageInvalide) return () => { vivant = false; };
    void fetch(`/api/ged/ged/correspondants/${ligne.documentId}/releve?${requete}`, { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) return r.json();
        const j = await r.json().catch(() => null);
        throw new Error(j?.error?.message || '');
      })
      .then((j) => { if (vivant) { setD(j); if (!j) setEchec('Le relevé n’a pas pu être calculé.'); } })
      .catch((e: Error) => { if (vivant) setEchec(e.message || 'Le relevé n’a pas pu être calculé.'); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [ligne.documentId, requete, tour, plageInvalide]);

  const r = d?.releve;
  const dest = ligne.destinatairesQualite;
  const courrielPret = d?.courriel?.configure === true;
  /* Le bouton grisé DIT pourquoi : c'est exactement ce qui manquait aux deux
     flux publics avant le lot des notifications — on cliquait, l'écran
     remerciait, et rien ne partait. */
  /* Produire et adresser le relevé : le RQ et le CQ (le serveur fait foi). Un
     correspondant désactivé n'en reçoit plus (14/10/2026). */
  const peutAdresser = d?.peutEcrire === true;
  const desactive = ligne.actif === false;
  const empechement = desactive
    ? 'Ce correspondant est désactivé : on ne lui adresse plus de relevé. Réactivez-le d’abord si c’est voulu.'
    : !dest.adresses.length
    ? 'Ce correspondant n’a aucune adresse de courriel : renseignez-en une dans sa fiche, ou téléchargez le PDF.'
    : !courrielPret
      ? `Aucun serveur d’envoi n’est réglé — ${d?.courriel?.consequence || 'rien ne partira'}. Le PDF reste disponible.`
      : '';

  /**
   * Un geste d'écriture, qui ACCEPTE d'être renvoyé une seconde fois.
   *
   * Le serveur répond 409 quand un relevé existe déjà pour cette période : on
   * pose alors la question en nommant ce qui est déjà parti, et l'on rejoue le
   * même appel avec `confirmerSecond`. Le drapeau porte sur CE geste-ci, jamais
   * sur un réglage durable — « ne plus me prévenir » aurait éteint
   * l'avertissement aussi pour les doublons qui sont des fautes.
   */
  const ecrire = async (chemin: string) => {
    const appel = (second: boolean) => fetchEcriture(
      `/api/ged/ged/correspondants/${ligne.documentId}/releve/${chemin}`,
      {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(second ? { ...periode, confirmerSecond: true } : periode),
      },
    );
    const rep = await appel(false);
    if (rep.status !== 409) return rep;
    const j = await rep.json().catch(() => null);
    const ok = await demander(j?.error?.message || 'Un relevé a déjà été adressé pour cette période. En adresser un second ?',
      { titre: 'Relevé déjà adressé', confirmer: 'Adresser quand même' });
    if (!ok) return null;
    return appel(true);
  };

  const telecharger = () => run('pdf', async () => {
    const rep = await ecrire('pdf');
    if (!rep) return;
    if (!rep.ok) {
      const j = await rep.json().catch(() => null);
      showToast(j?.error?.message
        || 'Le PDF n’a pas pu être produit. Le relevé reste consultable ici et exportable en CSV.', 'erreur');
      return;
    }
    const blob = await rep.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `releve-${ligne.code}-${d?.releve.periode.cle || mois}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast('Relevé produit — il est enregistré au registre des relevés, et les non-conformités portent la trace.', 'info');
    setTour((t) => t + 1);
  });

  const envoyer = () => run('mail', async () => {
    const rep = await ecrire('envoyer');
    if (!rep) return;
    const j = await rep.json().catch(() => null);
    if (!rep.ok) { showToast(j?.error?.message || 'L’envoi n’a pas abouti.', 'erreur'); return; }
    /* Les DEUX faits : ce qui est parti et ce qui ne l'est pas. Un message qui
       ne dirait que le succès laisserait un échec partiel invisible. */
    const parts = [
      j?.envoyes?.length ? `Envoyé à ${j.envoyes.join(', ')}.` : 'Aucun envoi n’a abouti.',
      j?.echecs?.length ? `Refusé pour ${j.echecs.map((e: { adresse: string; motif: string }) => `${e.adresse} (${e.motif})`).join(' ; ')}.` : '',
      j?.repli ? 'Adresse de repli utilisée — aucune adresse « qualité » n’est déclarée.' : '',
      j?.pdfJoint === false ? 'Le PDF n’a pas pu être joint : le corps du message porte le tableau.' : '',
    ].filter(Boolean);
    showToast(parts.join(' '), j?.ok ? 'info' : 'erreur');
    setTour((t) => t + 1);
  });

  /* Même règle que le formulaire : le panneau s'ouvre en haut de page alors que
     la ligne cliquée est plus bas — on l'amène à l'écran. */
  const racine = useRef<HTMLDivElement>(null);
  useEffect(() => { racine.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, [ligne.documentId]);

  /* La clé joint la référence et le rang : deux dossiers de la MÊME
     non-conformité partageraient sinon une clé, et React réutiliserait la
     mauvaise ligne au changement de mois. */
  const lignesReleve = useMemo(() => (r?.lignes || []).map((l, i) => ({ ...l, cle: `${l.ncRef}-${i}` })), [r]);

  return (
    <div ref={racine} style={{ ...card, padding: 18, display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          Relevé — {ligne.nom}
        </h2>
        <span style={{ flex: 1 }} />
        <Bouton taille="sm" onClick={onFerme}>Fermer</Bouton>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'grid', gap: 5 }}>
          <span style={lbl}><Help aide={AIDE_PERIODE}>Ce qu’on adresse</Help></span>
          <Segment ariaLabel="Période du relevé" valeur={mode} onChange={setMode}
            options={[
              { valeur: 'mois', libelle: 'Mois' },
              { valeur: 'plage', libelle: 'Période' },
              { valeur: 'selection', libelle: 'Sélection', compteur: choix.length || undefined },
            ]} />
        </div>
        {mode === 'mois' && (
          <div>
            <label style={lbl} htmlFor="rel-mois">Mois</label>
            <input id="rel-mois" type="month" value={mois} onChange={(e) => setMois(e.target.value)}
              style={{ ...inputStyle, maxWidth: 190 }} />
          </div>
        )}
        {mode === 'plage' && (<>
          <div>
            <label style={lbl} htmlFor="rel-du">Du</label>
            <input id="rel-du" type="date" value={du} max={au || undefined} onChange={(e) => setDu(e.target.value)}
              style={{ ...inputStyle, maxWidth: 170 }} />
          </div>
          <div>
            <label style={lbl} htmlFor="rel-au">Au (inclus)</label>
            <input id="rel-au" type="date" value={au} min={du || undefined} onChange={(e) => setAu(e.target.value)}
              style={{ ...inputStyle, maxWidth: 170 }} />
          </div>
        </>)}
        <span style={{ flex: 1 }} />
        {peutAdresser && (
          <Bouton taille="sm" variante="primaire" chargement={estOccupe('pdf')}
            disabled={estOccupe() || chargement || plageInvalide || selectionVide || desactive}
            title={desactive ? empechement : selectionVide ? 'Cochez au moins une non-conformité.' : plageInvalide ? 'La période est incomplète ou inversée.' : undefined}
            onClick={() => void telecharger()}>
            <I n="download" s={13} />Télécharger le PDF
          </Bouton>
        )}
        <Bouton taille="sm" disabled={estOccupe() || chargement || plageInvalide || selectionVide}
          onClick={() => {
            window.location.href = `/api/ged/ged/correspondants/${ligne.documentId}/releve.csv?${requete}`;
          }}>
          Exporter (CSV)
        </Bouton>
        {peutAdresser && (
          <Bouton taille="sm" chargement={estOccupe('mail')}
            disabled={estOccupe() || chargement || !!empechement || plageInvalide || selectionVide}
            title={empechement || (selectionVide ? 'Cochez au moins une non-conformité.' : `Envoyer à ${dest.adresses.join(', ')}`)}
            onClick={() => void envoyer()}>
            <I n="mail" s={13} />Envoyer par courriel
          </Bouton>
        )}
      </div>

      {plageInvalide && <div style={encart}>La période doit avoir un premier et un dernier jour, dans l’ordre.</div>}

      {mode === 'selection' && d && !chargement && (
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
            <span style={lbl}>Non-conformités imputées à {ligne.nom}</span>
            <span style={{ flex: 1 }} />
            {d.imputees.length > 0 && (
              <Bouton taille="sm" variante="discret"
                onClick={() => setChoix(choix.length === d.imputees.length ? [] : d.imputees.map((n) => n.ref))}>
                {choix.length === d.imputees.length ? 'Tout décocher' : 'Tout cocher'}
              </Bouton>
            )}
          </div>
          {d.imputees.length === 0 ? (
            <EmptyState titre="Aucune non-conformité ne lui est imputée."
              indice="Une non-conformité s’impute à un correspondant dans sa déclaration (« L’erreur vient-elle d’un correspondant extérieur ? »). Seules les NC retenues figurent au relevé." />
          ) : d.imputees.map((n) => {
            const coche = choix.includes(n.ref);
            return (
              <label key={n.ref} style={{
                display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer',
                padding: '6px 8px', borderRadius: 'var(--radius-sm)',
                background: coche ? 'var(--cyan-50)' : 'transparent',
              }}>
                <input type="checkbox" checked={coche} style={{ marginTop: 3 }}
                  onChange={(e) => setChoix(e.target.checked ? [...choix, n.ref] : choix.filter((x) => x !== n.ref))} />
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, whiteSpace: 'nowrap' }}>{n.ref}</span>
                <span style={{ flex: 1 }}>{n.titre || <em style={{ color: 'var(--text-muted)' }}>sans intitulé</em>}</span>
                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {fmtDate(n.date)} · {n.dossiers ? `${n.dossiers} dossier(s)` : 'non détaillée'}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {/* ⚠️ L'AVERTISSEMENT SE LIT AVANT LE CLIC. La confirmation du second
          envoi arrive de toute façon au moment du geste, mais la découvrir là
          est trop tard pour changer de période : on vient de choisir un mois
          déjà adressé sans le savoir. */}
      {d?.dejaAdresse?.existe && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>Déjà adressé.</strong> {d.dejaAdresse.phrase}{' '}
          En produire un second reste possible — il vous sera demandé de le confirmer.
        </div>
      )}
      {empechement && <div style={encart}>{empechement}</div>}
      {dest.repli && !empechement && (
        <div style={encart}>
          Aucune adresse « relevés et signalements qualité » n’est déclarée : l’envoi partirait à
          l’adresse <strong>{dest.adresses.join(', ')}</strong>, qui n’est pas celle de la personne qui
          répond d’un écart.
        </div>
      )}

      {chargement && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Calcul du relevé…</p>}
      {echec && <div style={encart}>{echec}</div>}

      {r && !chargement && (<>
        <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
          <strong>{d?.titre}</strong> — {r.compte.ncs} non-conformité(s) · {r.compte.dossiers} dossier(s) concerné(s)
          {r.compte.moisPrecedents.length > 0 && (
            <span style={{ color: 'var(--text-muted)' }}>
              {' '}· rappel : {r.compte.moisPrecedents.map((m) => `${m.mois} — ${m.dossiers}`).join(' · ')}
            </span>
          )}
        </div>

        {/* En mode « sélection », un relevé vide ne dit rien : la liste à cocher
            est juste au-dessus. Sinon, le tableau porte son état vide. */}
        {r.lignes.length === 0 && mode === 'selection' ? null : (
          /* L7 vague 2 (11/10/2026) : registre SIMPLE sur le socle. L'export reste
             celui du SERVEUR (« Exporter (CSV) », avec l'en-tête du relevé) : un
             seul export par registre. */
          <Tableau
            id="correspondants-releve"
            ajusterLargeur
            niveau="simple"
            lignes={lignesReleve}
            cleLigne={(l) => l.cle}
            vide={{
              titre: d?.phraseVide || 'Aucune non-conformité ne lui est imputée sur cette période.',
              indice: 'Ce n’est pas une erreur : c’est un résultat, et le document le dira en toutes lettres si vous l’adressez.',
            }}
            colonnes={[
              { cle: 'date', libelle: 'Date', type: 'date', valeur: (l) => l.date, rendu: (l) => fmtDate(l.date) },
              { cle: 'ncRef', libelle: 'Référence', valeur: (l) => l.ncRef, style: { fontFamily: 'var(--font-mono)', fontWeight: 700 }, rendu: (l) => l.ncRef },
              { cle: 'ncTitre', libelle: 'Constat', valeur: (l) => l.ncTitre, rendu: (l) => l.ncTitre },
              { cle: 'dossier', libelle: 'Dossier concerné', valeur: (l) => l.dossier || '', rendu: (l) => l.dossier || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>non détaillé</span> },
              { cle: 'gravite', libelle: 'Gravité', type: 'liste', valeur: (l) => l.gravite, rendu: (l) => l.gravite },
              { cle: 'statut', libelle: 'État', type: 'liste', valeur: (l) => l.statut, rendu: (l) => l.statut },
              { cle: 'suites', libelle: 'Suites', valeur: (l) => l.suites || '', rendu: (l) => l.suites || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>en cours</span> },
            ]}
          />
        )}

        {(d?.anterieurs?.length ?? 0) > 0 && (
          <div style={{ display: 'grid', gap: 5 }}>
            <span style={lbl}>Relevés déjà produits</span>
            {d?.anterieurs.map((a) => (
              <div key={a.ref} style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{a.ref}</span>
                {' — '}{a.periode} · {a.dossiers ?? 0} dossier(s) ·{' '}
                {a.envoye
                  ? <>envoyé le {fmtDate(a.dateEnvoi)} à {a.destinataires.join(', ')}</>
                  : <>PDF produit le {fmtDate(a.datePrepare)} — <span style={{ color: 'var(--text-muted)' }}>remis par le laboratoire, hors plateforme</span></>}
              </div>
            ))}
          </div>
        )}
      </>)}
      {confirmation}
    </div>
  );
}

/* ───────────────────────────── L'écran ───────────────────────────── */

export function Correspondants({ famille: familleInitiale = 'toutes' }: { famille?: string } = {}) {
  const [d, setD] = useState<Carnet | null>(null);
  const [chargement, setChargement] = useState(true);
  const [echec, setEchec] = useState(false);
  const [tour, setTour] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  /* L'ONGLET : une famille de contacts, ou toutes (10/10/2026, carnet de contacts). */
  const [famille, setFamille] = useState(familleInitiale);
  const [saisie, setSaisie] = useState(false);
  const [edite, setEdite] = useState<Ligne | null>(null);
  const [releve, setReleve] = useState<Ligne | null>(null);
  /* LA FICHE DU CONTACT (RQ, 12/10/2026) : un clic sur le contact déplie ses
     conventions et ses événements — les consulter, en ajouter. */
  const [ouverte, setOuverte] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/correspondants', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) { setD(j); setEchec(!j); } })
      .catch(() => { if (vivant) { setD(null); setEchec(true); } })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);

  const recharger = () => {
    /* ⚠️ Le carnet est mis en cache pour les formulaires de non-conformité
       (`ChoixCorrespondant`) : sans cet oubli, le tiers qu'on vient d'inscrire
       n'apparaîtrait dans la liste de déclaration qu'après un rechargement de
       page — et l'on croirait que l'inscription n'a pas pris. */
    oublierCarnet();
    setSaisie(false); setEdite(null); setTour((t) => t + 1);
  };

  const filtrees = useMemo(() => {
    const base = (d?.lignes || []).filter((l) => (
      filtre === 'sanscourriel' ? !l.destinatairesQualite.adresses.length
        : filtre === 'avecnc' ? l.compte.ncs > 0
          : filtre === 'inactifs' ? !l.actif
            : l.actif))
      .filter((l) => famille === 'toutes' || (l.familles || []).includes(famille));
    const qq = q.trim().toLowerCase();
    return qq
      ? base.filter((l) => `${l.nom} ${l.code} ${l.ville} ${l.contact} ${(l.rubriques || {}).fournit || ''}`.toLowerCase().includes(qq))
      : base;
  }, [d, filtre, q, famille]);

  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement du carnet de contacts…</p>;
  if (echec || !d) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <EnteteEcran rubrique="Calendrier & contacts" clauseISO="7.2" titre="Carnet de contacts" />
        <div style={encart}>
          Le carnet n’a pas pu être chargé. Ce message n’est <strong>pas</strong> « aucun
          correspondant » : l’écran ne sait rien pour l’instant.
        </div>
      </div>
    );
  }

  const { compte, peutEcrire } = d;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <EnteteEcran rubrique="Calendrier & contacts" clauseISO="7.2" titre="Carnet de contacts"
        actions={peutEcrire && compte.total > 0 && !saisie && !edite
          ? <BoutonCreer onClick={() => { setSaisie(true); setReleve(null); }}>Nouveau contact</BoutonCreer>
          : undefined} />

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Tous les tiers du laboratoire en un seul carnet : laboratoires (sous-traités, clients,
        confrères), cliniques et centres de dialyse, médecins prescripteurs extérieurs, préleveurs,
        fournisseurs et prestataires, organismes d’assurance. Cliquez sur un contact pour consulter
        ou ajouter ses <strong>conventions</strong> et ses <strong>événements</strong> : ils
        s’affichent au calendrier du laboratoire. Il porte l’<strong>identité</strong> et les coordonnées — rien
        de plus. Le jugement reste à « Revue de contrat &amp; évaluations » : la compétence d’un
        laboratoire sous-traité (<RefISO code="6.7" />), le contrat et l’évaluation d’un fournisseur.
      </p>

      {d.orphelines.length > 0 && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>
            {d.orphelines.length} non-conformité(s) imputée(s) à un code que le carnet ne connaît pas
          </strong>{' '}
          ({[...new Set(d.orphelines.map((o) => o.correspondantCode))].join(', ')}). Elles
          n’apparaîtront dans aucun relevé : recréez la ligne avec ce code exact, ou corrigez
          l’imputation sur la fiche de chaque non-conformité.
        </div>
      )}

      {famille === 'laboratoires' && d.catalogue?.sousTraites > 0 && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>{d.catalogue.sousTraites} examen{d.catalogue.sousTraites > 1 ? 's' : ''} du catalogue déclaré{d.catalogue.sousTraites > 1 ? 's' : ''} sous-traité{d.catalogue.sousTraites > 1 ? 's' : ''}</strong>
          {' '}({d.catalogue.codes.slice(0, 12).join(', ')}{d.catalogue.codes.length > 12 ? '…' : ''}). Le laboratoire qui les reçoit se nomme
          dans l’objet de son contrat, à « Revue de contrat &amp; évaluations ».
        </div>
      )}

      {compte.total === 0 && !saisie && (
        <EmptyState
          titre="Le carnet de contacts est vide."
          indice="Commencez par celui avec qui le laboratoire travaille tous les mois — le centre de dialyse, par exemple. Le nom, le type et une adresse de courriel suffisent pour pouvoir lui adresser un relevé."
          action={peutEcrire ? <Bouton variante="primaire" taille="sm" onClick={() => setSaisie(true)}>Inscrire un contact</Bouton> : undefined} />
      )}

      {saisie && <Formulaire ligne={null} catalogues={d.catalogues} onFait={recharger} onAnnule={() => setSaisie(false)} />}
      {edite && <Formulaire ligne={edite} catalogues={d.catalogues} onFait={recharger} onAnnule={() => setEdite(null)} />}
      {releve && <PanneauReleve ligne={releve} aujourdhui={d.aujourdhui} onFerme={() => setReleve(null)} />}

      {compte.total > 0 && (
        <Segment ariaLabel="Famille de contacts" valeur={famille} onChange={setFamille}
          options={[
            { valeur: 'toutes', libelle: 'Tous', compteur: d.lignes.filter((l) => l.actif).length },
            ...d.catalogues.familles.map((fa) => ({
              valeur: fa.code, libelle: fa.label,
              compteur: d.lignes.filter((l) => l.actif && (l.familles || []).includes(fa.code)).length,
            })),
          ]} />
      )}
      {compte.total > 0 && (
        <FilterBar
          q={q} onQ={setQ} placeholder="Rechercher un contact…"
          chips={[
            { k: 'tous', label: 'Actifs', n: compte.actifs },
            { k: 'avecnc', label: 'Avec non-conformités', n: compte.avecNc },
            { k: 'sanscourriel', label: 'Sans courriel', n: compte.sansCourriel },
            { k: 'inactifs', label: 'Inactifs', n: compte.total - compte.actifs },
          ]}
          actif={filtre} onChip={setFiltre}
          total={compte.total} affiches={filtrees.length}
          onReset={() => { setQ(''); setFiltre('tous'); }} />
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {filtrees.map((l) => (
          <div key={l.documentId} style={{ ...card, padding: 14, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setOuverte(ouverte === l.documentId ? null : l.documentId)}
                aria-expanded={ouverte === l.documentId} title={ouverte === l.documentId ? 'Replier la fiche' : 'Ouvrir la fiche : conventions et événements'}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 14, fontWeight: 700, color: 'var(--text-strong)', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>
                {l.nom}
              </button>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)' }}>{l.code}</span>
              {l.types.map((t) => (
                <React.Fragment key={t}>
                  {pill({
                    label: d.catalogues.types.find((x) => x.code === t)?.label || t,
                    bg: 'var(--cyan-50)', fg: 'var(--color-primary-active)',
                  })}
                </React.Fragment>
              ))}
              {!l.actif && pill({ label: 'Inactif', bg: 'var(--gray-50)', fg: 'var(--text-muted)' })}
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" aria-expanded={ouverte === l.documentId} onClick={() => setOuverte(ouverte === l.documentId ? null : l.documentId)}>
                Conventions &amp; événements{d.conventions?.[l.code]?.length ? ` (${d.conventions[l.code].length})` : ''} {ouverte === l.documentId ? '▴' : '▾'}
              </Bouton>
              <Bouton taille="sm" onClick={() => { setReleve(l); setEdite(null); setSaisie(false); }}>
                Relevé mensuel →
              </Bouton>
              {l.modifiable && (
                <Bouton taille="sm" aria-label={`Modifier ${l.nom}`}
                  onClick={() => { setEdite(l); setSaisie(false); setReleve(null); }}>
                  <I n="pen" s={12} />Modifier
                </Bouton>
              )}
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-body)' }}>
              {!(l.familles || []).includes('fournisseurs') && <span>{SENS[l.sens] || l.sens}</span>}
              {l.rubriques?.fournit && <span>Fournit : <strong>{l.rubriques.fournit}</strong></span>}
              {(d.conventions?.[l.code] || []).filter((c) => c.actif && c.nature !== 'evenement').map((c) => (
                <span key={c.nom}>Convention : <strong>{c.description}</strong></span>
              ))}
              {l.contact && <span>Contact : <strong>{l.contact}</strong></span>}
              {l.telephone && <span>{l.telephone}</span>}
              {l.ville && <span>{l.ville}</span>}
              <span>
                Non-conformités imputées : <strong>{l.compte.ncs}</strong>
                {l.compte.dossiers > 0 && <> · {l.compte.dossiers} dossier(s)</>}
                {l.compte.ouvertes > 0 && <> · {l.compte.ouvertes} non clôturée(s)</>}
              </span>
              {/* Ses deux autres rôles (14/10/2026) — ils n'entrent pas au relevé. */}
              {(l.compte.reclamations || 0) > 0 && <span>Réclamations faites : <strong>{l.compte.reclamations}</strong></span>}
              {(l.compte.signalees || 0) > 0 && <span>Écarts signalés : <strong>{l.compte.signalees}</strong></span>}
            </div>
            {l.destinatairesQualite.adresses.length > 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Relevé qualité → <strong>{l.destinatairesQualite.adresses.join(', ')}</strong>
                {l.destinatairesQualite.repli && ' (adresse de repli : aucune adresse « qualité » déclarée)'}
              </div>
            ) : (
              <div style={{ fontSize: 12.5, color: 'var(--amber-700)', fontWeight: 600 }}>
                Aucune adresse : aucun relevé ne pourra lui être envoyé.
              </div>
            )}
            {l.manques.length > 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>À compléter : {l.manques.join(' · ')}.</div>
            )}
            {ouverte === l.documentId && (
              <ConventionsContact contact={{ code: l.code, nom: l.nom }} modifiable={l.modifiable} />
            )}
          </div>
        ))}
        {compte.total > 0 && filtrees.length === 0 && (
          <EmptyState titre="Aucun correspondant ne correspond à ces critères."
            indice="Élargissez la recherche ou revenez à « Actifs »."
            action={<Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser</Bouton>} />
        )}
      </div>
    </div>
  );
}
